import React, { useState, useEffect, useMemo } from 'react';
import './DashboardCreate.css';
import FindCreatorsPanel from './FindCreatorsPanel';
import FullCalendarView from './FullCalendarView';
import CreatorProgressModal from './CreatorProgressModal';
import { useNotifications } from '../Context/NotificationContext';
import { useChat } from '../Chat/ChatContext';
import { getAuth } from 'firebase/auth';
import { collection, getDocs, onSnapshot, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { ProjectIdGenerator } from '../utils/projectIdGenerator';
import { addBusinessDays } from '../utils/dateUtils';


function DashboardCreate({ setActiveTab, setStartBriefForCampaign, selectedCampaign }) {
  const [showFindPanel, setShowFindPanel] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  const [activeBriefTab, setActiveBriefTab] = useState('');
  const [activeStatusTab, setActiveStatusTab] = useState('all');
  const [showCreatorModal, setShowCreatorModal] = useState(false);
  const [selectedCreator, setSelectedCreator] = useState(null);
  const { createCreatorProgressNotification } = useNotifications();
  const { getUnreadCount, getChatForProject } = useChat();
  const [realCampaignData, setRealCampaignData] = useState([]);
  const [isLoadingCreators, setIsLoadingCreators] = useState(true);
  const [submissionsData, setSubmissionsData] = useState(new Map()); // Map: "campaignId/briefId" -> Map(projectId -> submissionData)

  // Real-time listener for all campaigns with creators from Firestore
  useEffect(() => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
      setIsLoadingCreators(false);
      return;
    }

    setIsLoadingCreators(true);

    // Set up real-time listener for campaigns
    const campaignsRef = collection(db, 'users', user.uid, 'campaigns');
    const unsubscribeCampaigns = onSnapshot(campaignsRef, async (campaignsSnapshot) => {
      try {
        const allCampaignData = [];

        for (const campaignDoc of campaignsSnapshot.docs) {
          const campaignData = campaignDoc.data();
          
          // Set up listener for briefs in this campaign
          const briefsRef = collection(db, 'users', user.uid, 'campaigns', campaignDoc.id, 'briefs');
          const briefsSnapshot = await getDocs(briefsRef); // Using getDocs for now, can optimize with onSnapshot
          
          const briefsWithCreators = [];

          for (const briefDoc of briefsSnapshot.docs) {
            const briefData = briefDoc.data();
            const creators = briefData.creators || [];
            
            // Get submissions from state (updated in real-time by separate listener)
            const submissionsKey = `${campaignDoc.id}/${briefDoc.id}`;
            const submissionsMap = submissionsData.get(submissionsKey) || new Map();
            
            if (creators.length > 0) {
              // Calculate progress and status for each creator
              const creatorsWithProgress = creators.map(creator => {
                const progress = creator.progress || {};
                let progressPercentage = 0;
                
                // Calculate progress based on completed steps (exclude non-step fields)
                const steps = ['productReceived', 'filming', 'editing', 'submitted'];
                const completedSteps = steps.filter(step => progress[step] === true).length;
                progressPercentage = steps.length > 0 ? Math.round((completedSteps / steps.length) * 100) : 0;

                // Use existing projectId only; never generate here
                const rawProjectId = creator.projectId || creator.progress?.projectId || null;
                const projectId = ProjectIdGenerator.normalizeProjectId(rawProjectId) || rawProjectId || null;

                const normalizedBriefId = typeof briefDoc.id === 'string' ? briefDoc.id.trim() : briefDoc.id;
                const normalizedCampaignId = typeof campaignDoc.id === 'string' ? campaignDoc.id.trim() : campaignDoc.id;

                // Determine effective status - if progress shows submitted, override status
                const effectiveStatus = (progress.submitted === true) ? 'submitted' : (creator.status || 'assigned');

                // Check if this creator has a submission - match ONLY by projectId
                let submissionId = null;
                let submissionData = null;
                if (projectId) {
                  // Try exact match with normalized projectId
                  if (submissionsMap.has(projectId)) {
                    submissionData = submissionsMap.get(projectId);
                    submissionId = submissionData.submissionId;
                  } else {
                    // Try matching by projectId equivalence (handles variations in formatting)
                    for (const [submissionProjectId, subData] of submissionsMap) {
                      try {
                        if (ProjectIdGenerator.areEquivalent(submissionProjectId, projectId)) {
                          submissionData = subData;
                          submissionId = subData.submissionId;
                          break;
                        }
                      } catch (e) {
                        // If comparison fails, continue
                      }
                    }
                  }
                } else {
                  // No projectId on creator - cannot match (old system fallback removed)
                  console.warn(`⚠️ Creator ${creator.name || creator.handle} (${creator.id}) missing projectId - cannot match to submission`);
                }

                // Calculate due date - if submitted, use 5 business day review period from submission date
                let dueText = 'No deadline';
                let isOverdue = false;
                
                if (effectiveStatus === 'submitted' && submissionData && submissionData.submittedAt) {
                  // Calculate days left for review (5 business days from submission)
                  const submittedAt = submissionData.submittedAt?.toDate?.() ?? submissionData.submittedAt;
                  const reviewDueDate = submissionData.dueDate || addBusinessDays(new Date(submittedAt), 5);
                  const today = new Date();
                  const daysLeft = Math.ceil((reviewDueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                  
                  if (daysLeft < 0) {
                    dueText = `${Math.abs(daysLeft)} days overdue`;
                    isOverdue = true;
                  } else if (daysLeft === 0) {
                    dueText = 'Due today';
                  } else {
                    dueText = `${daysLeft} days`;
                  }
                } else {
                  // Use creator deadline for non-submitted status
                  const effectiveDeadline = creator.extendedDeadline || creator.deadline;
                  
                  if (effectiveDeadline) {
                    const deadline = new Date(effectiveDeadline);
                    const today = new Date();
                    const daysLeft = Math.ceil((deadline - today) / (1000 * 60 * 60 * 24));
                    if (daysLeft < 0) {
                      dueText = `${Math.abs(daysLeft)} days overdue`;
                      isOverdue = true;
                    } else if (daysLeft === 0) {
                      dueText = 'Due today';
                    } else {
                      dueText = `${daysLeft} days`;
                    }
                  } else if (creator.status === 'awaiting_approval') {
                    dueText = 'Pending acceptance';
                  }
                }
                
                // Debug: Log if submission found for submitted creators
                if (effectiveStatus === 'submitted' && !submissionId) {
                  console.log('⚠️ Creator has submitted status but no submission ID found:', {
                    creatorId: creator.id,
                    projectId: projectId,
                    submissionsMapSize: submissionsMap.size,
                    submissionsMapKeys: Array.from(submissionsMap.keys())
                  });
                }

                return {
                  id: creator.id,
                  projectId: projectId, // NEW: Unique project identifier
                  creator: creator.handle || creator.name,
                  name: creator.name,
                  avatar: creator.avatar || creator.profileImage || '/creatorimages/creatorpic1v2-min.png',
                  status: effectiveStatus,
                  statusText: getStatusText(effectiveStatus),
                  progress: progressPercentage,
                  due: dueText,
                  isOverdue,
                  lastUpdated: creator.lastProgressUpdate || creator.assignedAt || new Date().toISOString(),
                  hasNewMessage: false, // TODO: Implement message tracking
                  tier: creator.tier,
                  briefId: normalizedBriefId,
                  briefTitle: briefData.title || briefData.name,
                  campaignId: normalizedCampaignId,
                  campaignName: campaignData.name,
                  scriptIds: creator.scriptIds || [],
                  assignedAt: creator.assignedAt,
                  deadline: creator.deadline,
                  progressData: progress,
                  graceRequest: creator.graceRequest || null,
                  extendedDeadline: creator.extendedDeadline || null,
                  deliveryAddress: creator.deliveryAddress 
                    || creator.progress?.deliveryAddress 
                    || (creator.shippingDetails ? {
                      addressLine1: creator.shippingDetails.addressLine1,
                      addressLine2: creator.shippingDetails.addressLine2 || '',
                      city: creator.shippingDetails.city,
                      state: creator.shippingDetails.state || '',
                      postalCode: creator.shippingDetails.postalCode,
                      country: creator.shippingDetails.country,
                      contactPerson: creator.shippingDetails.contactPerson || creator.name,
                      phone: creator.shippingDetails.phone || '',
                      specialInstructions: creator.shippingDetails.specialInstructions || ''
                    } : null),
                  productType: creator.productType || (creator.serviceOrProduct === 'Product' ? 'product' : 'service'),
                  serviceOrProduct: creator.serviceOrProduct,
                  submissionId: submissionId,
                  submissionData: submissionData // Store submission data including status
                };
              });

              briefsWithCreators.push({
                id: briefDoc.id,
                brief: briefData.title || briefData.name || 'Untitled Brief',
                creators: creatorsWithProgress
              });
            }
          }

          if (briefsWithCreators.length > 0) {
            allCampaignData.push({
              id: campaignDoc.id,
              campaign: campaignData.name || campaignDoc.id,
              briefs: briefsWithCreators
            });
          }
        }

        setRealCampaignData(allCampaignData);
        setIsLoadingCreators(false);
      } catch (error) {
        console.error('Error fetching creator progress:', error);
        setIsLoadingCreators(false);
      }
    });

    return () => {
      if (unsubscribeCampaigns) {
        unsubscribeCampaigns();
      }
    };
  }, [submissionsData]); // Re-run when submissions data changes

  // Real-time listener for submissions across all campaigns
  useEffect(() => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;

    const submissionListeners = new Map(); // Store unsubscribe functions

    // Set up listeners for all campaigns and briefs - react to campaign changes
    const setupSubmissionListeners = async () => {
      try {
        // Clean up old listeners for campaigns/briefs that no longer exist
        const currentCampaignBriefs = new Set();
        
        const campaignsRef = collection(db, 'users', user.uid, 'campaigns');
        const campaignsSnapshot = await getDocs(campaignsRef);

        for (const campaignDoc of campaignsSnapshot.docs) {
          const briefsRef = collection(db, 'users', user.uid, 'campaigns', campaignDoc.id, 'briefs');
          const briefsSnapshot = await getDocs(briefsRef);

          for (const briefDoc of briefsSnapshot.docs) {
            const submissionsKey = `${campaignDoc.id}/${briefDoc.id}`;
            currentCampaignBriefs.add(submissionsKey);
            
            // Skip if listener already exists
            if (submissionListeners.has(submissionsKey)) continue;

            const submissionsRef = collection(
              db,
              'users',
              user.uid,
              'campaigns',
              campaignDoc.id,
              'briefs',
              briefDoc.id,
              'submissions'
            );

            // Set up real-time listener for submissions
            const unsubscribe = onSnapshot(submissionsRef, (submissionsSnapshot) => {
              const submissionsMap = new Map();
              
              submissionsSnapshot.docs.forEach(submissionDoc => {
                const submissionData = submissionDoc.data();
                const submissionProjectId = submissionData.projectId ? 
                  (ProjectIdGenerator.normalizeProjectId(submissionData.projectId) || submissionData.projectId) : null;
                
                if (submissionProjectId) {
                  const submittedAt = submissionData.submittedAt?.toDate ? 
                    submissionData.submittedAt.toDate() : 
                    (submissionData.submittedAt instanceof Date ? 
                      submissionData.submittedAt : 
                      (submissionData.submittedAt ? new Date(submissionData.submittedAt) : null));
                  
                  const dueDate = submissionData.dueDate?.toDate ? 
                    submissionData.dueDate.toDate() : 
                    (submissionData.dueDate instanceof Date ? 
                      submissionData.dueDate : 
                      (submissionData.dueDate ? new Date(submissionData.dueDate) : null));
                  
                  submissionsMap.set(submissionProjectId, {
                    submissionId: submissionDoc.id,
                    submittedAt: submittedAt,
                    dueDate: dueDate || (submittedAt ? addBusinessDays(new Date(submittedAt.getTime?.() ?? submittedAt), 5) : null),
                    status: submissionData.status
                  });
                }
              });

              // Update submissions data state
              setSubmissionsData(prev => {
                const newMap = new Map(prev);
                newMap.set(submissionsKey, submissionsMap);
                return newMap;
              });
            });

            submissionListeners.set(submissionsKey, unsubscribe);
          }
        }

        // Clean up listeners for campaigns/briefs that no longer exist
        submissionListeners.forEach((unsubscribe, key) => {
          if (!currentCampaignBriefs.has(key)) {
            unsubscribe();
            submissionListeners.delete(key);
          }
        });
      } catch (error) {
        console.error('Error setting up submission listeners:', error);
      }
    };

    // Set up listeners initially and when campaigns change
    const campaignsRef = collection(db, 'users', user.uid, 'campaigns');
    const unsubscribeCampaigns = onSnapshot(campaignsRef, () => {
      setupSubmissionListeners();
    });

    // Initial setup
    setupSubmissionListeners();

    // Cleanup function
    return () => {
      unsubscribeCampaigns();
      submissionListeners.forEach(unsubscribe => unsubscribe());
      submissionListeners.clear();
    };
  }, []); // Only run once on mount

  // Helper function to get status text
  const getStatusText = (status) => {
    switch (status) {
      case 'awaiting_approval': return 'Pending';
      case 'accepted':
      case 'confirmed': return 'Awaiting Product';
      case 'filming': return 'Filming';
      case 'editing': return 'Editing';
      case 'submitted': return 'Submitted';
      case 'rejected': return 'Rejected';
      default: return 'Assigned';
    }
  };

  // Mock data removed - using only real Firestore data
  const mockCampaignData = [];

  const handleCreatorCardClick = (creator) => {
    // Format creator data for the modal (matching the format from DashboardCampaigns)
    const normalizedBriefId = typeof creator.briefId === 'string' ? creator.briefId.trim() : creator.briefId;
    const normalizedCampaignId = typeof creator.campaignId === 'string' ? creator.campaignId.trim() : creator.campaignId;
    const formattedCreator = {
      id: creator.id,
      projectId: creator.projectId,
      creator: creator.creator, // Handle
      name: creator.name,
      avatar: creator.avatar,
      status: creator.status,
      tier: creator.tier,
      campaignId: normalizedCampaignId,
      campaignName: creator.campaignName,
      briefId: normalizedBriefId,
      briefName: creator.briefTitle || creator.briefName,
      scriptIds: creator.scriptIds || [],
      scriptCount: creator.scriptIds ? creator.scriptIds.length : 0,
      due: creator.due,
      isOverdue: creator.isOverdue,
      assignedAt: creator.assignedAt,
      deadline: creator.deadline,
      progress: creator.progressData || {
        productReceived: false,
        filming: false,
        editing: false,
        submitted: false
      },
      graceRequest: creator.graceRequest || null,
      extendedDeadline: creator.extendedDeadline || null,
      deliveryAddress: creator.deliveryAddress || creator.progress?.deliveryAddress || null,
      productType: creator.productType
    };
    
    console.log('Opening modal with creator data:', formattedCreator);
    setSelectedCreator(formattedCreator);
    setShowCreatorModal(true);
  };

  const handleCloseCreatorModal = () => {
    setShowCreatorModal(false);
    setSelectedCreator(null);
  };


  const getStatusColor = (status) => {
    switch (status) {
      case 'awaiting': return '#3399ff';
      case 'filming': return '#42bcff';
      case 'editing': return '#42bcff';
      case 'review': return '#9c27b0';
      case 'submitted': return '#5cd68d';
      default: return '#64748b';
    }
  };

  const getStatusBackgroundColor = (status, isOverdue) => {
    if (isOverdue) return '#dc2626'; // Red for overdue
    switch (status) {
      case 'filming':
      case 'editing':
        return '#42bcff'; // Blue for filming/editing
      case 'review':
      case 'awaiting':
        return '#3399ff'; // Blue for review/awaiting
      case 'submitted':
        return '#5cd68d'; // Green for submitted
      default:
        return '#64748b'; // Gray for default
    }
  };

  const getStatusDotColor = (status, isOverdue) => {
    if (isOverdue) return 'white'; // White dot for red background
    switch (status) {
      case 'filming':
      case 'editing':
        return 'white'; // White dot for yellow background
      case 'review':
      case 'awaiting':
        return 'white'; // White dot for blue background
      case 'submitted':
        return 'white'; // White dot for green background
      default:
        return 'white'; // White dot for gray background
    }
  };

  const getProgressStages = (creator) => {
    const stages = [
      { name: 'Brief Submitted', completed: true },
      { name: 'Accepted/Awaiting Product/Service', completed: creator.status !== 'awaiting' },
      { name: 'Filming', completed: ['filming', 'editing', 'review', 'submitted'].includes(creator.status) },
      { name: 'Editing', completed: ['editing', 'review', 'submitted'].includes(creator.status) },
      { name: 'Submitted', completed: creator.status === 'submitted' }
    ];
    return stages;
  };

  const getProgressPercentage = (creator) => {
    const stages = getProgressStages(creator);
    const completedStages = stages.filter(stage => stage.completed).length;
    return (completedStages / stages.length) * 100;
  };

  // Get stepper state for creator
  const getStepperState = (creator) => {
    const status = creator.status;
    // Prioritize progressData (object) over progress (which might be a percentage number)
    const progress = creator.progressData || (typeof creator.progress === 'object' ? creator.progress : {});
    // Check productType, and also check serviceOrProduct as fallback
    // Default to 'product' if unknown (safer - won't mark as received prematurely)
    const productType = creator.productType || (creator.serviceOrProduct === 'Product' ? 'product' : creator.serviceOrProduct === 'Service' ? 'service' : 'product');
    
    const stages = [
      { key: 'accepted', label: 'Accepted', completed: status !== 'awaiting' && status !== 'awaiting_approval' },
      { 
        key: 'received', 
        label: 'Received', 
        completed: (() => {
          // For services, mark as received when status is confirmed/accepted or beyond
          if (productType === 'service') {
            return ['confirmed', 'accepted', 'filming', 'editing', 'review', 'submitted'].includes(status);
          }
          // For products, only mark as received when creator has confirmed receipt
          // Do NOT mark as received if status indicates product hasn't been received yet
          if (['product_sent', 'awaiting_product', 'confirmed', 'accepted'].includes(status)) {
            return false;
          }
          // Only mark as received if explicitly confirmed by creator
          return progress.productReceived === true || creator.productReceived === true;
        })()
      },
      { key: 'filming', label: 'Filming', completed: ['editing', 'review', 'submitted'].includes(status) || progress.filming === true },
      { key: 'editing', label: 'Editing', completed: ['review', 'submitted'].includes(status) || progress.editing === true }
    ];
    
    const activeStageIndex = stages.findIndex(stage => !stage.completed);
    const allCompleted = stages.every(stage => stage.completed);
    
    return { stages, activeStageIndex, allCompleted };
  };

  // Get creator status (Pro or Rising Star)
  const getCreatorStatus = (creator) => {
    // Use real tier data from Firestore
    const isPro = creator.tier === 'Pro';
    
    return {
      type: isPro ? 'pro' : 'rising',
      text: creator.tier || 'Rising Star',
      class: isPro ? 'creator-status-pro' : 'creator-status-rising'
    };
  };

  // Use real campaign data if available, otherwise fall back to mock data
  // Memoize to prevent unnecessary recalculations and ensure approved creators are filtered before render
  // This prevents the flash where approved creators appear briefly before being filtered out
  const activeCampaignData = useMemo(() => {
    if (realCampaignData.length === 0) return mockCampaignData;
    
    // Filter out approved creators at the data level to prevent flash
    // This ensures approved creators never appear in the UI, even during initial render
    return realCampaignData.map(campaign => ({
      ...campaign,
      briefs: campaign.briefs.map(brief => ({
        ...brief,
        creators: brief.creators.filter(creator => {
          // Exclude creators with approved submissions immediately
          // Check both submissionData.status and ensure it's not undefined/null
          const submissionStatus = creator.submissionData?.status;
          const hasApprovedSubmission = submissionStatus === 'approved';
          return !hasApprovedSubmission;
        })
      })).filter(brief => brief.creators.length > 0) // Remove empty briefs
    })).filter(campaign => campaign.briefs.length > 0); // Remove empty campaigns
  }, [realCampaignData]);

  // Get recently updated creators (all creators sorted by lastUpdated, excluding approved)
  const getRecentlyUpdatedCreators = () => {
    const allCreators = activeCampaignData.flatMap(campaign => 
      campaign.briefs.flatMap(brief => 
        brief.creators
          .filter(creator => {
            // Only include creators who have been approved/accepted (exclude awaiting_approval, rejected, and completed)
            const creatorStatus = creator.status;
            // Note: Approved submissions are already filtered out in activeCampaignData memoization
            return creatorStatus && 
                   creatorStatus !== 'awaiting_approval' &&
                   creatorStatus !== 'rejected' &&
                   creatorStatus !== 'completed' &&
                   creatorStatus !== 'cancelled';
          })
          .map(creator => ({
            ...creator,
            campaignName: campaign.campaign,
            briefName: brief.brief
          }))
      )
    );
    
    return allCreators
      .sort((a, b) => new Date(b.lastUpdated) - new Date(a.lastUpdated))
      .slice(0, 6); // Show top 6 recently updated
  };

  // Get all creators for calendar (including approved ones)
  const getAllCreatorsForCalendar = () => {
    return activeCampaignData.flatMap(campaign => 
      campaign.briefs.flatMap(brief => 
        brief.creators
          .filter(creator => {
            // Only include creators who have been approved/accepted (exclude awaiting_approval, rejected, completed, cancelled)
            const creatorStatus = creator.status;
            return creatorStatus && 
                   creatorStatus !== 'awaiting_approval' &&
                   creatorStatus !== 'rejected' &&
                   creatorStatus !== 'completed' &&
                   creatorStatus !== 'cancelled';
          })
          .map(creator => ({
            ...creator,
            campaignName: campaign.campaign,
            briefName: brief.brief
          }))
      )
    );
  };

  // Get filtered creators based on selected campaign and brief tab
  const getFilteredCreators = () => {
    if (!selectedCampaignId) return [];
    
    const selectedCampaign = activeCampaignData.find(c => c.id === selectedCampaignId || c.id === parseInt(selectedCampaignId));
    if (!selectedCampaign) return [];
    
    if (!activeBriefTab) {
      // Return all creators from the selected campaign (only approved/accepted)
      return selectedCampaign.briefs.flatMap(brief => 
        brief.creators
          .filter(creator => {
            // Only include creators who have been approved/accepted (exclude awaiting_approval, rejected, completed, cancelled)
            const creatorStatus = creator.status;
            return creatorStatus && 
                   creatorStatus !== 'awaiting_approval' &&
                   creatorStatus !== 'rejected' &&
                   creatorStatus !== 'completed' &&
                   creatorStatus !== 'cancelled';
          })
          .map(creator => ({
            ...creator,
            campaignName: selectedCampaign.campaign,
            briefName: brief.brief
          }))
      );
    }
    
    // Return creators from the selected brief (only approved/accepted)
    const selectedBrief = selectedCampaign.briefs.find(b => b.id === activeBriefTab || b.id === parseInt(activeBriefTab));
    if (!selectedBrief) return [];
    
    return selectedBrief.creators
      .filter(creator => {
        // Only include creators who have been approved/accepted (exclude awaiting_approval, rejected, completed, cancelled)
        const creatorStatus = creator.status;
        return creatorStatus && 
               creatorStatus !== 'awaiting_approval' &&
               creatorStatus !== 'rejected' &&
               creatorStatus !== 'completed' &&
               creatorStatus !== 'cancelled';
      })
      .map(creator => ({
        ...creator,
        campaignName: selectedCampaign.campaign,
        briefName: selectedBrief.brief
      }));
  };

  // Get available briefs for selected campaign
  const getAvailableBriefs = () => {
    if (!selectedCampaignId) return [];
    const selectedCampaign = activeCampaignData.find(c => c.id === selectedCampaignId || c.id === parseInt(selectedCampaignId));
    return selectedCampaign ? selectedCampaign.briefs : [];
  };

  // Get status-based filtered creators
  const getStatusFilteredCreators = (creators) => {
    if (activeStatusTab === 'all') return creators;
    
    return creators.filter(creator => {
      switch (activeStatusTab) {
        case 'needs-action':
          // Ready to review OR has new messages OR shipping details provided but product not shipped
          const hasShippingDetails = creator.deliveryAddress 
            || creator.progress?.deliveryAddress 
            || creator.shippingDetails;
          const productNotShipped = creator.productType === 'product' 
            && hasShippingDetails 
            && !creator.progress?.productSent 
            && creator.status !== 'product_sent';
          const needsShippingAction = productNotShipped 
            && (creator.status === 'confirmed' || creator.status === 'accepted' || creator.status === 'awaiting_product');
          
          return ((creator.status === 'review' || creator.status === 'awaiting' || creator.hasNewMessage) && !creator.isOverdue) 
            || needsShippingAction;
        case 'overdue':
          return creator.isOverdue;
        default:
          return true;
      }
    });
  };

  // Get status tab counts
  const getStatusTabCounts = (creators) => {
    const counts = {
      all: creators.length,
      'needs-action': creators.filter(c => {
        const hasShippingDetails = c.deliveryAddress 
          || c.progress?.deliveryAddress 
          || c.shippingDetails;
        const productNotShipped = c.productType === 'product' 
          && hasShippingDetails 
          && !c.progress?.productSent 
          && c.status !== 'product_sent';
        const needsShippingAction = productNotShipped 
          && (c.status === 'confirmed' || c.status === 'accepted' || c.status === 'awaiting_product');
        
        return ((c.status === 'review' || c.status === 'awaiting' || c.hasNewMessage) && !c.isOverdue) 
          || needsShippingAction;
      }).length,
      overdue: creators.filter(c => c.isOverdue).length
    };
    return counts;
  };

  const filteredCreators = getFilteredCreators();
  const availableBriefs = getAvailableBriefs();
  const recentlyUpdatedCreators = getRecentlyUpdatedCreators();
  const allCreatorsForCalendar = getAllCreatorsForCalendar();

  // Determine what to display for cards
  const displayCreators = selectedCampaignId ? filteredCreators : recentlyUpdatedCreators;
  const statusFilteredCreators = getStatusFilteredCreators(displayCreators);
  const statusCounts = getStatusTabCounts(displayCreators);
  const displayTitle = selectedCampaignId ? 'Creator Progress' : 'Recently Updated';
  const displayCount = statusFilteredCreators.length;

  // For calendar, always use all creators (including approved ones)
  const calendarCreators = selectedCampaignId ? filteredCreators : allCreatorsForCalendar;

  const handleCampaignChange = (e) => {
    setSelectedCampaignId(e.target.value);
    setActiveBriefTab(''); // Reset brief tab when campaign changes
  };

  const handleBriefTabClick = (briefId) => {
    setActiveBriefTab(activeBriefTab === briefId ? '' : briefId);
  };

  const handleStatusTabClick = (status) => {
    setActiveStatusTab(status);
  };

  return (
    <div className="dashboard-create-container">
      {/* Header Section */}
      <div className="dashboard-create-overview-container">
        <div className="dashboard-create-overview-header">
          <div className="dashboard-create-header-content">
            <div className="dashboard-create-header-text">
              <h1 className="dashboard-create-overview-title">Creator Hub</h1>
              <p className="dashboard-create-overview-subtitle">Manage creator relationships and track project progress</p>
            </div>
            <button className="find-creators-btn" onClick={() => setShowFindPanel(true)}>
              + Find Creators
            </button>
          </div>
        </div>

        <div className="dashboard-create-content">
        {/* Creator Progress Section */}
        <div className="creator-progress-section">
          <div className="section-header">
            <h3>{displayTitle}</h3>
            <span className="creator-count">
              {displayCount} creator{displayCount !== 1 ? 's' : ''}
                  </span>
        </div>

          {/* Campaign Dropdown */}
          <div className="campaign-dropdown-container">
            <div className="dropdown-group">
              <label htmlFor="campaign-select" className="dropdown-label">Campaign</label>
              <select 
                id="campaign-select"
                value={selectedCampaignId}
                onChange={handleCampaignChange}
                className="dropdown-select"
              >
                <option value="">Recently Updated</option>
                {activeCampaignData.map(campaign => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.campaign}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Brief Tabs - Only show when campaign is selected */}
            {selectedCampaignId && availableBriefs.length > 0 && (
              <div className="brief-tabs-container">
                <div className="brief-tabs">
                  <button
                    className={`brief-tab ${!activeBriefTab ? 'active' : ''}`}
                    onClick={() => setActiveBriefTab('')}
                  >
                    All Briefs
                  </button>
                  {availableBriefs.map(brief => (
                    <button
                      key={brief.id}
                      className={`brief-tab ${activeBriefTab === brief.id.toString() ? 'active' : ''}`}
                      onClick={() => handleBriefTabClick(brief.id.toString())}
                    >
                      {brief.brief}
                      <span className="brief-creator-count">({brief.creators.length})</span>
                    </button>
                  ))}
                </div>
                </div>
            )}
                </div>
          
          {/* Status Tabs */}
          <div className="status-tabs-container">
            <div className="status-tabs">
              <button
                className={`status-tab ${activeStatusTab === 'all' ? 'active' : ''}`}
                onClick={() => handleStatusTabClick('all')}
              >
                All
                <span className="status-count">{statusCounts.all}</span>
              </button>
              <div className="tab-divider"></div>
              <button
                className={`status-tab needs-action ${activeStatusTab === 'needs-action' ? 'active' : ''}`}
                onClick={() => handleStatusTabClick('needs-action')}
              >
                Needs Action
                <span className="status-count">{statusCounts['needs-action']}</span>
              </button>
              <div className="tab-divider"></div>
              <button 
                className={`status-tab overdue ${activeStatusTab === 'overdue' ? 'active' : ''}`}
                onClick={() => handleStatusTabClick('overdue')}
              >
                Overdue
                <span className="status-count">{statusCounts.overdue}</span>
              </button>
            </div>
          </div>
          
          {/* Results Section */}
          {statusFilteredCreators.length > 0 ? (
            <div className="creator-results">
              <div className="creator-progress-grid">
                {statusFilteredCreators.map(creator => (
                  <div 
                    key={creator.projectId || `${creator.id}-${creator.briefId}`}
                    className="creator-progress-card"
                    onClick={() => handleCreatorCardClick(creator)}
                  >
                    <div className="create-creator-header">
                      <div className="create-avatar-container">
                        <img src={creator.avatar} alt={creator.creator} className="create-creator-avatar" />
                      </div>
                      <div className="create-creator-info">
                        <h4 className="create-creator-name">{creator.creator}</h4>
                        <div className={`creator-status-tag ${getCreatorStatus(creator).class}`}>
                          <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                            <path d="M8 0L10.5 5.5L16 8L10.5 10.5L8 16L5.5 10.5L0 8L5.5 5.5L8 0Z" fill="currentColor"/>
                          </svg>
                          {getCreatorStatus(creator).text}
                        </div>
                        <p className="campaign-name">
                          {creator.campaignName}
                          {creator.briefName && ` • ${creator.briefName}`}
                        </p>
                      </div>
                      <div 
                        className="status-indicator"
                        style={{ 
                          backgroundColor: getStatusBackgroundColor(creator.status, creator.isOverdue)
                        }}
                      >
                        <span 
                          className="status-dot"
                          style={{ backgroundColor: getStatusDotColor(creator.status, creator.isOverdue) }}
                        ></span>
                      </div>
                    </div>
                    
                    <div className="progress-section">
                      <div className="progress-header">
                        <span className="status-text">{creator.statusText}</span>
                        <span className={`due-date ${creator.isOverdue ? 'overdue' : ''}`}>
                          {creator.due}
                        </span>
                      </div>
                      
                      {/* Progress Stepper */}
                      {(() => {
                        const stepperState = getStepperState(creator);
                        
                        if (stepperState.allCompleted) {
                          return (
                            <button 
                              className="video-ready-status clickable-ready"
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                // Always navigate to Hand-In page when "Video Ready to Review" is shown
                                // If submission ID exists, auto-open the submission review modal
                                console.log('Video Ready to Review clicked:', {
                                  creatorId: creator.id,
                                  submissionId: creator.submissionId,
                                  campaignId: creator.campaignId,
                                  briefId: creator.briefId,
                                  setActiveTabType: typeof setActiveTab
                                });
                                
                                // Always navigate to Hand-In - never open modal
                                if (creator.submissionId) {
                                  console.log('Navigating to Hand-In with submission ID:', creator.submissionId);
                                  setActiveTab('handin', {
                                    openSubmissionId: creator.submissionId,
                                    openCampaignId: creator.campaignId,
                                    openBriefId: creator.briefId
                                  });
                                } else {
                                  console.log('Navigating to Hand-In without submission ID');
                                  setActiveTab('handin');
                                }
                                
                                // Prevent any further event handling
                                return false;
                              }}
                            >
                              <div className="video-ready-icon">✓</div>
                              Video Ready to Review
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                          );
                        }
                        
                        return (
                          <div className="creator-progress-stepper">
                            {stepperState.stages.map((stage, index) => {
                              const isCompleted = stage.completed;
                              const isActive = index === stepperState.activeStageIndex;
                              const isPending = !isCompleted && !isActive;
                              
                              return (
                                <div key={stage.key} className="stepper-stage">
                                  <div className={`stage-indicator ${isCompleted ? 'completed' : isActive ? 'active' : 'pending'}`}></div>
                                  <div className={`stage-label ${isCompleted ? 'completed' : isActive ? 'active' : 'pending'}`}>
                                    {stage.label}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}

                      {/* Message Creator button */}
                      <button 
                        className="message-creator-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          // Open creator progress modal with chat panel active
                          setSelectedCreator({
                            ...creator,
                            briefId: typeof creator.briefId === 'string' ? creator.briefId.trim() : creator.briefId,
                            campaignId: typeof creator.campaignId === 'string' ? creator.campaignId.trim() : creator.campaignId,
                            projectId: typeof creator.projectId === 'string' ? creator.projectId.trim() : creator.projectId,
                            openChatPanel: true // Flag to open chat panel directly
                          });
                          setShowCreatorModal(true);
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Message Creator
                        {/* Show red dot when there are unread messages */}
                        {(() => {
                          const normalizedProjectId = creator.projectId ? ProjectIdGenerator.normalizeProjectId(creator.projectId) : null;
                          const chat = normalizedProjectId ? getChatForProject(normalizedProjectId) : null;
                          const unreadCount = chat ? getUnreadCount(chat.chatId) : 0;
                          if (unreadCount > 0) {
                            console.log(`Creator ${creator.name} has ${unreadCount} unread messages`);
                          }
                          return unreadCount > 0 && (
                            <span className="notification-dot">{unreadCount}</span>
                          );
                        })()}
                      </button>
                    </div>
                  </div>
            ))}
          </div>
        </div>
          ) : (
            <div className="no-results">
              <p>No creators found for the selected filters</p>
            </div>
          )}
        </div>

        {/* Calendar Section - Full Width */}
        <div className="calendar-section">
          <div className="section-header">
            <h3>Calendar</h3>
          </div>
          <div className="calendar-container">
            <FullCalendarView creatorData={calendarCreators} />
          </div>
        </div>
        </div>
      </div>

      {showFindPanel && <FindCreatorsPanel onClose={() => setShowFindPanel(false)} />}
      {showCreatorModal && selectedCreator && (
        <CreatorProgressModal
          isOpen={showCreatorModal}
          onClose={handleCloseCreatorModal}
          creatorData={selectedCreator}
        />
      )}


    </div>
  );
}

export default DashboardCreate;
